
// Re-export the singleton Supabase client to avoid multiple instances
import { supabase, SUPABASE_AUTH_STORAGE_KEY } from '@/integrations/supabase/client';
import { createPasswordResetSender } from './password-reset-request';
import { logError } from './sentry';
import { ReferralService } from './referral-service';
export { supabase } from '@/integrations/supabase/client';

let sendPasswordReset: ReturnType<typeof createPasswordResetSender> | undefined;

// Authentication helper functions
export const auth = {
  signUp: async (email: string, password: string, userData: any) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: userData,
        emailRedirectTo: `${window.location.origin}/auth/callback`
      },
    });
    return { data, error };
  },

  signInWithPassword: async (credentials: { email: string, password: string }) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email: credentials.email,
      password: credentials.password,
    });
    return { data, error };
  },

  signOut: async () => {
    const { error } = await supabase.auth.signOut();
    if (!error) {
      return { error: null };
    }

    logError('Sign out failed, attempting local cleanup', error);
    const { error: localError } = await supabase.auth.signOut({ scope: 'local' });
    if (localError) {
      logError('Local sign out cleanup failed', localError);
      return { error: localError };
    }

    return { error: null };
  },

  getCurrentUser: async () => {
    const { data, error } = await supabase.auth.getUser();
    return { user: data.user, error };
  },

  getSession: async () => {
    const { data, error } = await supabase.auth.getSession();
    return { session: data.session, error };
  },

  resetPassword: async (email: string) => {
    sendPasswordReset ??= createPasswordResetSender({
      supabaseUrl: import.meta.env.VITE_SUPABASE_URL,
      publishableKey: import.meta.env.VITE_SUPABASE_ANON_KEY,
      sessionStorageKey: SUPABASE_AUTH_STORAGE_KEY,
      storage: window.localStorage,
    });
    return sendPasswordReset(email, `${window.location.origin}/reset-password`);
  },

  // Enhanced function to ensure a user profile exists
  ensureUserProfile: async () => {
    try {
      const isAuthRoute =
        typeof window !== 'undefined' &&
        (window.location.pathname.startsWith('/auth') ||
          window.location.pathname.startsWith('/email-confirmation'));

      // Get the current user
      const { user, error } = await auth.getCurrentUser();
      if (error || !user) {
        if (!isAuthRoute) {
          logError('No authenticated user found in ensureUserProfile', error);
        }
        return { success: false, message: 'No authenticated user found' };
      }
      
      
      const metadata = user.user_metadata || {};

      // Check if user already exists in the users table
      const { data: existingUser, error: queryError } = await supabase
        .from('users')
        .select('*')
        .eq('id', user.id)
        .single();
      
      const referralCode =
        typeof metadata.referral_code === 'string' ? metadata.referral_code : undefined;

      // If user exists in users table, optionally record referral and return early
      if (existingUser && !queryError) {
        if (existingUser.user_type === 'creator' && referralCode) {
          await ReferralService.recordSignup(referralCode);
        }
        return { success: true, message: 'User profile already exists', userDetails: existingUser };
      }
      
      
      // User doesn't exist in users table, create profile
      // Extract metadata from the auth user
      const userType = metadata.user_type || 'creator'; // Default to creator
      const fullName = metadata.full_name || user.email?.split('@')[0] || 'Unknown';
      
      // Prepare user data for insertion - only include columns that exist in users table
      const userData: any = {
        id: user.id,
        email: user.email,
        user_type: userType,
        full_name: fullName,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };

      // Only add fields that exist in the users table schema
      if (metadata.company) {
        userData.company = metadata.company;
      }
      if (metadata.bio) {
        userData.bio = metadata.bio;
      }
      if (metadata.website) {
        userData.website = metadata.website;
      }
      if (metadata.avatar_url) {
        userData.avatar_url = metadata.avatar_url;
      }
      
      
      // Insert user into users table
      const { data: newUser, error: insertError } = await supabase
        .from('users')
        .insert(userData)
        .select()
        .single();
      
      if (insertError) {
        logError("Error creating user profile in users table", insertError);
        return { success: false, message: `Failed to create user profile: ${insertError.message}`, error: insertError };
      }
      

      // If this is a creator, also create a creator profile for creator-specific data
      if (userType === 'creator') {
        const { error: creatorError } = await supabase
          .from('creators')
          .insert({
            id: user.id,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          });

        if (creatorError) {
          logError("Error creating creator profile in creators table", creatorError);
          // Don't fail the whole process if creator profile creation fails
        }
      }

      // If this is a marketer, also create a marketer profile for marketer-specific data
      if (userType === 'marketer') {
        const { error: marketerError } = await supabase
          .from('marketers')
          .insert({
            billing_mode: 'platform_charged',
            id: user.id,
            service_fee: 30,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          });

        if (marketerError) {
          logError("Error creating marketer profile in marketers table", marketerError);
          // Don't fail the whole process if marketer profile creation fails
        }

        // Check for pending campaign invitations and update them
        const { data: pendingInvitations, error: invitationError } = await supabase
          .from('campaign_members')
          .select('id, campaign_id')
          .eq('invited_email', user.email)
          .is('user_id', null);

        if (!invitationError && pendingInvitations && pendingInvitations.length > 0) {
          // Update pending invitations with the new user's ID
          const { error: updateError } = await supabase
            .from('campaign_members')
            .update({ user_id: user.id })
            .eq('invited_email', user.email)
            .is('user_id', null);

          if (updateError) {
            logError("Error updating pending campaign invitations", updateError);
          } else {
            console.log(`Updated ${pendingInvitations.length} pending campaign invitation(s) for ${user.email}`);
          }
        }
      }

      if (userType === 'creator' && referralCode) {
        await ReferralService.recordSignup(referralCode);
      }

      return { success: true, message: 'User profile created successfully', userDetails: newUser };
    } catch (error: any) {
      logError('Error in ensureUserProfile', error);
      return { success: false, message: error.message || 'Failed to ensure user profile', error };
    }
  }
};
